import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { useStore } from 'react-redux';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import * as Forms from '../../../../components/form-elements/_index';
import { StateSelectList } from '../../../../components/utility/states';
import { useServiceCenter } from '../../../../hooks/use-service-center';
import { FlyoutSaveCancel } from '../../../../components/flyout-components/FlyoutSaveCancel';
import { FlyoutTitle } from '../../../../components/flyout-components/FlyoutTitle';
import { serviceCenterSelectList } from '../../../../selectors/userdata-selectors';
import { StatusCode } from '../../../../utils/api';
import { AppUtils } from '../../../../utils/app-utils';
import { Mapper } from '../../../../utils/mapper';
import { ModelValidator } from '../../../../utils/model-validator';
import { AssetStatus } from '../../../../transportation/models/enums/asset-status';
import { AssetType, AssetTypeMetadata } from '../../../../transportation/models/enums/asset-type';
import { TractorMakeMetadata } from '../../../../transportation/models/enums/tractor-make';
import { TractorTypeMetadata } from '../../../../transportation/models/enums/tractor-type';
import { TrailerMakeMetadata } from '../../../../transportation/models/enums/trailer-make';
import { TrailerTypeMetadata } from '../../../../transportation/models/enums/trailer-type';
import { BoxTruckCreate, TractorCreate, TrailerCreate } from '../../../../transportation/models/fleet/asset-create';
import { AssetRead, TractorRead, TrailerRead } from '../../../../transportation/models/fleet/asset-read';
import { AssetUpdate } from '../../../../transportation/models/fleet/asset-update';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps {
    visible     : boolean;
    onSuccess   : any;
    onClose     : any;
    asset      : AssetRead;
}

export const FleetActionEditAsset = (props: IProps) => {

    const store                                 = useStore();
    const userServiceCenter                     = useServiceCenter();
    const [service]                             = React.useState(new TransportationSvc());
    const [editAsset, setEditAsset]             = React.useState<AssetRead>(new AssetRead());
    const [errors, setErrors]                   = React.useState({} as any);
    const [saveAttempts, setSaveAttempts]       = React.useState(0);
    
    const [assetTypes] = React.useState(() => {
        return AppUtils.enumToSelectList(AssetTypeMetadata);
    });
    
    const [serviceCenters] = React.useState(() => {
        return serviceCenterSelectList(store.getState());
    });
    
    const [trailerTypes] = React.useState(() => {
        return AppUtils.enumToSelectList(TrailerTypeMetadata);
    });

    const [trailerMakes] = React.useState(() => {
        return AppUtils.enumToSelectList(TrailerMakeMetadata);
    });

    const [tractorTypes] = React.useState(() => {
        return AppUtils.enumToSelectList(TractorTypeMetadata);
    });

    const [tractorMakes] = React.useState(() => {
        return AppUtils.enumToSelectList(TractorMakeMetadata);
    });

    React.useEffect(() => {
        if (props.visible && !AppUtils.IsEmpty(props.asset)) {

            const tmpAsset = {...props.asset};
            
            if (!tmpAsset.id || tmpAsset.id <= 0) {
                tmpAsset.status = AssetStatus.Active;

                if (userServiceCenter.name !== 'ALL') {
                    tmpAsset.serviceCenterId = userServiceCenter.name;
                }
            }

            setEditAsset(tmpAsset);
        }
    }, [props.visible]);

    /**
     * Save button click handler
     */
    const onSave = async () => {
        const valid = checkValidity();
        if (valid) {
            const response = await service.updateAsset(editAsset);
            if (response.status === StatusCode.Success) {
                props.onSuccess(response.data);
            }
        }
    };

    /**
     * Cleanup some our state and close the panel
     */
    const onClose = () => {
        setEditAsset(undefined as unknown as AssetRead);
        setErrors({});
        setSaveAttempts(0);
        props.onClose();
    };

    /**
     * Cast the current Driver as the appropriate type
     * and run the model validator against it
     * @param d The AssetRead object to check
     */
    const checkValidity = () => {

        let tmpAsset;
        switch (editAsset.type) {
            case AssetType.Tractor:
                tmpAsset = !editAsset.id
                    ? Mapper.deserialize(TractorCreate, editAsset)
                    : Mapper.deserialize(AssetUpdate, editAsset);
                break;
            case AssetType.Trailer:
                tmpAsset = !editAsset.id
                    ? Mapper.deserialize(TrailerCreate, editAsset)
                    : Mapper.deserialize(AssetUpdate, editAsset);
                break;
            case AssetType.BoxTruck:
                tmpAsset = !editAsset.id
                    ? Mapper.deserialize(BoxTruckCreate, editAsset)
                    : Mapper.deserialize(AssetUpdate, editAsset);
                break;
            default:
                tmpAsset = Mapper.deserialize(AssetUpdate, editAsset);
        }

        const msgs = ModelValidator.validate(tmpAsset);
        setErrors(msgs);
        setSaveAttempts(saveAttempts + 1);

        return AppUtils.IsEmpty(msgs) ? true : false;
    };

    /**
     * Catch-all event handler for updating state
     * @param e The event from the input
     */
    const handleChange = (e : any) => {
        const tmpAsset : any = {...editAsset};

        if (e.target.name === "status") {
            tmpAsset[e.target.name] = e.target.checked ? AssetStatus.Active : AssetStatus.Inactive;
        }
        else if (e.target.name === "type" || 
                 e.target.name === "trailerMake" || 
                 e.target.name === "trailerType" ||
                 e.target.name === "tractorMake" ||
                 e.target.name === "tractorType") {
            tmpAsset[e.target.name] = Number.parseInt(e.target.value, 10);
        }
        else {
            tmpAsset[e.target.name] = e.target.value;
        }

        setEditAsset(tmpAsset);
    };

    const handleInServiceDateChange = (e : any) => {
        const tmpAsset : AssetRead = {...editAsset};
        tmpAsset.inServiceDate = e;
        setEditAsset(tmpAsset);
    };

    const handleOutServiceDateChange = (e : any) => {
        const tmpAsset : AssetRead = {...editAsset};
        tmpAsset.outOfServiceDate = e;
        setEditAsset(tmpAsset);
    };

    /**
     * Check if we've tried to save already, if so
     * trigger the validation check so the user gets
     * up to date feedback
     */
    const handleBlur = () => {
        if (saveAttempts > 0) {
            checkValidity();
        }
    };

    return <Drawer 
                anchor="right" 
                onClose={ onClose }
                open={ props.visible }>
                <div className="stop-action-drawer stop-action-basic-info">
                {
                    editAsset
                        ? <React.Fragment>
                            <FlyoutTitle 
                                onMobileClose={ onClose }
                                title={ editAsset.id > 0 ? "Edit Asset" : "Create Asset" } />

                            <div className="row">
                                 <div className="col-md-6">
                                    <Forms.SelectInput
                                        id="asset-type"
                                        label="Asset Type"
                                        name="type"
                                        error={ errors.type }
                                        disable={ editAsset.id ? true : false }
                                        onChange={handleChange}
                                        onBlur={ handleBlur }
                                        options={assetTypes}
                                        value={editAsset.type} />
                                </div>     
                                <div className="col-md-6">
                                    <Forms.SelectInput
                                        id="asset-service-center"
                                        label="Service Center"
                                        name="serviceCenterId"
                                        error={ errors.serviceCenterId }
                                        onChange={ handleChange }
                                        onBlur={ handleBlur }
                                        options={serviceCenters}
                                        disable={ (editAsset.id === 0 && userServiceCenter.name === "ALL") ? false : true }
                                        value={ editAsset.serviceCenterId } />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <Forms.TextInput
                                        id="asset-name"
                                        type="text"
                                        label="Name"
                                        name="name"
                                        disable={ editAsset.status === AssetStatus.Inactive }
                                        error={ errors.name }
                                        value={ editAsset.name }
                                        onChange={ handleChange }
                                        onBlur={ handleBlur }
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Forms.TextInput
                                        id="asset-year"
                                        type="number"
                                        label="Year"
                                        name="year"
                                        disable={ editAsset.status === AssetStatus.Inactive }
                                        error={ errors.year }
                                        value={ editAsset.year }
                                        onChange={ handleChange }
                                        onBlur={ handleBlur }
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Forms.TextInput
                                        id="asset-axleCount"
                                        type="number"
                                        label="Axle Count"
                                        name="axleCount"
                                        disable={ editAsset.status === AssetStatus.Inactive }
                                        error={ errors.axleCount }
                                        value={ editAsset.axleCount }
                                        onChange={ handleChange }
                                        onBlur={ handleBlur }
                                    />
                                </div>                                                          
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <DataGroupHeader title="License Information" />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <Forms.SelectInput
                                        id="asset-licensePlateState"
                                        label="License Plate State"
                                        name="licensePlateState"
                                        error={ errors.licensePlateState }
                                        onBlur={ handleBlur }
                                        onChange={ handleChange }
                                        options={ StateSelectList }
                                        disable={ editAsset.status === AssetStatus.Inactive }
                                        value={ editAsset.licensePlateState } />    
                                </div>
                                <div className="col-md-6">
                                    <Forms.TextInput
                                        id="asset-licensePlateNumber"
                                        type="text"
                                        label="License Plate Number"
                                        name="licensePlateNumber"
                                        disable={ editAsset.status === AssetStatus.Inactive }
                                        error={ errors.licensePlateNumber }
                                        value={ editAsset.licensePlateNumber }
                                        onChange={ handleChange }
                                        onBlur={ handleBlur }
                                    />
                                </div>  
                                <div className="col-md-6">
                                    <Forms.TextInput
                                        id="asset-vin"
                                        type="text"
                                        label="VIN"
                                        name="vin"
                                        disable={ editAsset.status === AssetStatus.Inactive }
                                        error={ errors.vin }
                                        value={ editAsset.vin }
                                        onChange={ handleChange }
                                        onBlur={ handleBlur }
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <DataGroupHeader title="Service Dates" />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <Forms.DateInput
                                        id="asset-inServiceDate"
                                        label="In-Service Date"
                                        name="inServiceDate"
                                        disable={ editAsset.status === AssetStatus.Inactive }
                                        error={ errors.inServiceDate }
                                        value={ editAsset.inServiceDate }
                                        onChange={ handleInServiceDateChange }
                                    />
                                </div>
                                {
                                    editAsset.id
                                        ? <div className="col-md-6">
                                            <Forms.DateInput
                                                id="asset-outOfServiceDate"
                                                label="Out Of Service Date"
                                                name="outOfServiceDate"
                                                disable={ editAsset.status === AssetStatus.Inactive }
                                                error={ errors.outOfServiceDate }
                                                value={ editAsset.outOfServiceDate }
                                                onChange={ handleOutServiceDateChange }
                                            />
                                        </div>
                                        : null
                                }
                            </div>

                            {
                                editAsset.type === AssetType.Trailer
                                    ? <React.Fragment>
                                        <div className="row">
                                            <div className="col-12">
                                                <DataGroupHeader title="Trailer Information" />
                                            </div>
                                        </div>
                                         <div className="row">
                                            <div className="col-md-6">
                                                <Forms.SelectInput
                                                    id="asset-trailerMake"
                                                    label="Trailer Make"
                                                    name="trailerMake"
                                                    error={ errors.trailerMake }
                                                    onChange={ handleChange }
                                                    onBlur={ handleBlur }
                                                    options={ trailerMakes }
                                                    disable={ editAsset.status === AssetStatus.Inactive }
                                                    value={ (editAsset as TrailerRead).trailerMake } />
                                            </div>
                                            <div className="col-md-6">
                                                <Forms.SelectInput
                                                    id="asset-trailerType"
                                                    label="Trailer Type"
                                                    name="trailerType"
                                                    error={ errors.trailerType }
                                                    disable={ editAsset.status === AssetStatus.Inactive }
                                                    onChange={handleChange}
                                                    onBlur={ handleBlur }
                                                    options={ trailerTypes }
                                                    value={ (editAsset as TrailerRead).trailerType} />
                                            </div>
                                        </div>
                                    </React.Fragment>
                                    : null
                            }

                            {
                                editAsset.type === AssetType.Tractor
                                    ? <React.Fragment>
                                        <div className="row">
                                            <div className="col-12">
                                                <DataGroupHeader title="Tractor Information" />
                                            </div>
                                        </div>
                                        <div className="row">
                                          <div className="col-md-6">
                                                <Forms.SelectInput
                                                    id="asset-tractorMake"
                                                    label="Tractor Make"
                                                    name="tractorMake"
                                                    error={ errors.tractorMake }
                                                    onChange={ handleChange }
                                                    onBlur={ handleBlur }
                                                    options={ tractorMakes }
                                                    disable={ editAsset.status === AssetStatus.Inactive }
                                                    value={ (editAsset as TractorRead).tractorMake } />
                                            </div>
                                            <div className="col-md-6">
                                                <Forms.SelectInput
                                                    id="asset-tractorType"
                                                    label="Tractor Type"
                                                    name="tractorType"
                                                    error={ errors.tractorType }
                                                    disable={ editAsset.status === AssetStatus.Inactive }
                                                    onChange={handleChange}
                                                    onBlur={ handleBlur }
                                                    options={ tractorTypes }
                                                    value={ (editAsset as TractorRead).tractorType} />
                                            </div>
                                        </div>
                                    </React.Fragment>
                                    : null
                            }

                            <div className="row">
                                <div className="col-12">
                                    <Forms.ToggleInput
                                        id="asset-status"
                                        label="Is Active?"
                                        name="status"                                
                                        onClick={ handleChange }
                                        checked={ editAsset.status === AssetStatus.Active } />
                                </div>
                            </div>

                        <FlyoutSaveCancel
                            onSave={ onSave }
                            onCancel={ onClose }
                        />
                    </React.Fragment>
                    : null
                }
                </div>
        </Drawer>;
};
